import { registerPlugin, Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { enqueueAudioSessionTask } from './audioSessionQueue';
import { noteInCallSessionEstablished, noteInCallSessionReleased } from '../utils/audioOutputBirth';

/**
 * AudioRouting — Capacitor plugin wrapper for in-call audio routing
 * and screen-lock control.
 *
 * Backed by AudioRoutingPlugin on Android (Java) and iOS (Swift).
 * On web — and in any context where the native plugin isn't registered —
 * every method becomes a no-op so callers don't need feature-detection
 * boilerplate.
 *
 * Beta-feedback (#7): mobile WebRTC routes to earpiece by default once
 * getUserMedia fires; users in the empathy circle audio room reported
 * the audio sounded muffled / quiet on join. This plugin gives the JS
 * layer a way to flip routing to loudspeaker.
 */
/** What a participant can ASK for. A headset is never a request — see AudioOutput. */
export type PreferredAudioOutput = 'speaker' | 'earpiece';

/**
 * What the OS actually routed to. 'headset' is reachable only as an ANSWER:
 * wired/Bluetooth/USB output wins over the preference (that rule fixed the
 * beta bug "headphones stopped playing, went to speakerphone"), so it is a
 * state the UI must be able to render but never a state it can request.
 */
export type AudioOutput = PreferredAudioOutput | 'headset';

/**
 * What became of a setAudioOutput request.
 *
 * A UNION RATHER THAN `AudioOutput | null`, because `null` was answering four
 * different questions with one word: the native call rejected, a newer tap
 * replaced this one before it ran, there is no native side at all, and — had
 * anyone read it that way — "nothing happened". Only ONE of those is a refusal
 * of something a person asked for, and only that one may be shown to her:
 *
 *   applied      the route moved; `effective` is where it actually went, which
 *                a connected headset can make different from the request.
 *   failed       the native side refused (bad value, no AudioManager, a
 *                deallocated plugin, an OEM exception). The user tapped and the
 *                audio did not move — she has to be told, because a tap that
 *                silently does nothing is indistinguishable from a dead button.
 *   superseded   a newer intent replaced this one while it sat in the queue.
 *                NOT a refusal: the newer call's own promise carries the
 *                outcome, and an error here would be a lie about a request
 *                that was never denied.
 *   unsupported  NOTHING WAS EVER ATTEMPTED, on either of the two ways that
 *                can happen: off native (no OS was asked), or the device has
 *                no such built-in output to route to — an Android tablet or
 *                Chromebook with no earpiece, an iPad with no receiver. The
 *                second one used to resolve `applied`: Android substituted the
 *                loudspeaker when `earpiece` was requested and
 *                setCommunicationDevice(speaker) answered true, and iOS's
 *                `overrideOutputAudioPort(.none)` simply stayed on the speaker
 *                without throwing. Either way the button stayed lit with no
 *                refusal, for a switch that was never physically possible.
 *                (Codex P2 on PR #871, id 3981030935.) Distinct from `failed`
 *                because nothing was refused and nothing malfunctioned — a
 *                retry cannot help — and because the control for it should not
 *                be on screen at all: see `hasEarpiece`.
 *
 * This is CLAUDE.md class B: a refusal that cannot be told apart from a normal
 * result does not reach anybody, whatever the caller intends to do with it.
 */
export type SetAudioOutputResult =
  | { status: 'applied'; effective: AudioOutput }
  | { status: 'failed' }
  | { status: 'superseded' }
  | { status: 'unsupported' };

/**
 * One snapshot of where the call's audio is, as the native side sees it.
 *
 * NAMED, and this is not cosmetic: the exact same object arrives by TWO routes
 * now — a polled `getStatus()` and the `routeChange` EVENT — and both native
 * plugins build it from one `statusPayload()` helper for the same reason. Two
 * shapes for one fact is how a listener and a poll start disagreeing about the
 * device.
 */
export interface AudioRouteStatus {
  speakerphoneOn: boolean;
  hasWiredHeadset: boolean;
  hasBluetoothA2dp: boolean;
  /**
   * Android only. Whether we currently hold audio focus.
   *
   * Losing focus is how Android quietly attenuates our stream when another
   * app plays a notification — the mechanism is literally called ducking, and
   * it is a live candidate for the reported "volume dampened after round 2".
   * Surfacing it here is what turns "it went quiet" into a timestamped row in
   * the diagnostics rather than something only the listener noticed.
   */
  hasAudioFocus?: boolean;
  /** Android AUDIOFOCUS_* constant from the last focus change. */
  lastFocusChange?: number;
  /**
   * Where call audio is going RIGHT NOW, as one word, on both platforms.
   *
   * The UI renders this rather than assuming its own last request stuck: a
   * headset overrides the preference, and the OS can move the route out from
   * under us (a charger or a headset plugged mid-call). Optional because an
   * older native build — an installed APK that has not been updated — has no
   * such field, and a Speaker button is not worth a crash.
   */
  output?: AudioOutput;
  /**
   * ANDROID ONLY: the same route as `output`, finer — `loudspeaker`,
   * `earpiece`, `wired`, `bluetooth`, or `type-N` for a device type the plugin
   * does not name. The route control reads it for one thing only: to draw a
   * Bluetooth headset as Bluetooth rather than as headphones. Absent on iOS,
   * where a headset is drawn as headphones.
   */
  route?: string;
  /**
   * What was last ASKED for, which is not always what happened — a headset
   * beats it. Diagnostic only: the UI renders `output`. Same optionality
   * rule as `output` (absent on an older native build).
   */
  preferredOutput?: PreferredAudioOutput;
  /**
   * Whether this device HAS a built-in earpiece at all.
   *
   * Many Android tablets and most Chromebooks do not, and neither does any
   * iPad. On those the speaker/earpiece control offers a position the hardware
   * cannot reach: Android substituted the loudspeaker for a requested earpiece
   * and reported success, iOS declined the override and stayed on the speaker.
   * Both left the button lit with no refusal, which is the dead button this
   * whole control exists to remove. (Codex P2 on PR #871, id 3981030935.)
   *
   * Measured from the SAME device enumeration the router chooses from, so the
   * capability the UI reads and the capability the router acts on are one
   * answer (class C): Android reads TYPE_BUILTIN_EARPIECE out of
   * getAvailableCommunicationDevices() on API 31+ and getDevices() below it;
   * iOS reads the device idiom (see the plugin — there is no availableOutputs
   * on AVAudioSession) with the live route as positive evidence.
   *
   * OPTIONAL, and `undefined` MUST NOT be read as false: an older installed
   * build has no such field, and hiding the toggle for every one of those
   * would be a worse regression than the bug. Only an explicit `false` hides
   * the control. Off native there is no status at all, and no control either.
   */
  hasEarpiece?: boolean;
  /**
   * Android's `AudioManager.getMode()` at the moment of the snapshot.
   *
   * The one fact that separates "a headset beat your request" from "there was
   * no call to route": outside `MODE_IN_COMMUNICATION` there is no in-call
   * route at all, so Android substitutes the media route and reports success.
   * Diagnostic only — no control branches on it; it is what lets a diagnostic
   * surface name the cause instead of guessing one (dev sound panel).
   *
   * Optional, and iOS never sends it: absent means "not known", never
   * "MODE_NORMAL".
   */
  mode?: number;
  /**
   * WHICH AUDIO USAGES ARE ACTUALLY PLAYING — the one field here that is not a
   * report of what this plugin asked for.
   *
   * Every other field describes the plugin's own request, or the device the OS
   * agrees it was asked to use. None of them could see the 2026-09-15 defect:
   * in a live 1:1 on an SM-G981U1 this app's player was `usage=USAGE_MEDIA`,
   * where the circle on the same device was `USAGE_VOICE_COMMUNICATION`.
   * `setCommunicationDevice()` steers only the communication strategy, so on
   * MEDIA every routing call was a no-op that reported success — the plugin
   * said "speaker", the OS agreed it had been asked for the speaker, and the
   * sound was on a strategy neither was looking at. A `"media"` entry in here
   * DURING a call is that bug, visible without a USB cable.
   *
   * Android only, and NOT scoped to this app: no public API attributes a
   * playback configuration to a uid. While a call holds exclusive audio focus
   * the set is ours in practice; the uid-attributed answer is `dumpsys audio`,
   * which is what `scripts/device/audio-route-litmus.sh` asserts on. Values are
   * `"voice_communication"`, `"media"`, or `"usage_<n>"` for anything else.
   *
   * Absent on iOS (where `sessionCategory`/`sessionMode` answer the same
   * question), on an older native build, and whenever the platform refused the
   * query — a diagnostic that can fail must not also be able to throw.
   */
  activePlaybackUsages?: string[];
  /**
   * THE TWO STREAMS, AND WHY BOTH. Android only.
   *
   * `MODE_IN_COMMUNICATION` points the volume keys at `STREAM_VOICE_CALL`, and
   * a player typed `USAGE_MEDIA` plays on `STREAM_MUSIC`. When the two come
   * apart, the person is holding a volume rocker that moves a stream nothing is
   * playing on — which is exactly what happened on 2026-09-17: the circle's
   * cues were on `STREAM_MUSIC` at 1 of 15 while the call stream sat at 8 of 8,
   * and the report was "the cues are silent".
   *
   * `activePlaybackUsages` says WHICH stream the sound is on; these say how loud
   * that stream is. Neither alone can tell an inaudible cue from a missing one,
   * so both are beaconed. Reported with their maxima because the scale is
   * device-specific — 1/7 and 1/15 are different volumes.
   *
   * Absent on iOS and on an older native build.
   */
  voiceCallVolume?: number;
  /** See `voiceCallVolume`. */
  voiceCallVolumeMax?: number;
  /** See `voiceCallVolume`. */
  musicVolume?: number;
  /** See `voiceCallVolume`. */
  musicVolumeMax?: number;
  /**
   * iOS only. The live `AVAudioSession` category and mode, as their raw
   * strings.
   *
   * The iOS sibling of `activePlaybackUsages`, and the same question in the
   * platform's own terms: playback is on the voice path only while the category
   * is `playAndRecord` (with `voiceChat`), and `overrideOutputAudioPort` — the
   * whole earpiece/speaker lever — silently does nothing outside it. A category
   * of `soloAmbient` here during a call is the iOS shape of the Android bug.
   *
   * Absent on Android, and on an older native build.
   */
  sessionCategory?: string;
  /** iOS only — see `sessionCategory`. */
  sessionMode?: string;
}

/**
 * `AudioManager.MODE_IN_COMMUNICATION`. The framework constant, not a value
 * worth re-deriving at a call site — and the only mode in which asking for the
 * earpiece can be honoured.
 */
export const ANDROID_MODE_IN_COMMUNICATION = 3;

/** The one event this plugin emits. */
const ROUTE_CHANGE_EVENT = 'routeChange';

interface AudioRoutingPluginInterface {
  /**
   * Sets up or tears down the whole VoIP audio session. Answers with the SAME
   * status payload as getStatus() and the routeChange event — one shape for
   * every route report in the plugin (class C). Nothing reads it today; the
   * service returns void.
   */
  setSpeakerphoneOn(options: { enabled: boolean }): Promise<AudioRouteStatus>;
  /**
   * Move the call's output between the loudspeaker and the earpiece WITHOUT
   * touching the audio session. `effective` is what the OS actually routed to,
   * which differs from `output` whenever a headset is connected.
   */
  setAudioOutput(options: { output: PreferredAudioOutput }): Promise<{
    output: PreferredAudioOutput;
    effective: AudioOutput;
    /**
     * Which of the two non-failure outcomes this was. RESOLVED rather than
     * rejected, because `unsupported` is not a malfunction: the OS was asked
     * for nothing it could refuse, the device simply has no such output. It
     * rides in the resolve payload instead of a `reject(msg, code)` so the
     * discriminator crosses the bridge as ordinary data on both platforms
     * rather than as an error field whose marshalling differs between them.
     *
     * Absent on a native build older than this field — which is exactly the
     * build that also substitutes silently — so its absence means `applied`.
     */
    outcome?: 'applied' | 'unsupported';
  }>;
  setKeepScreenOn(options: { enabled: boolean }): Promise<{ keepScreenOn: boolean }>;
  startBackgroundAudio(options: { title?: string; text?: string }): Promise<{ started: boolean }>;
  stopBackgroundAudio(): Promise<{ stopped: boolean }>;
  /**
   * Re-apply the route the native side already has recorded as preferred.
   * A CORRECTION, NOT A REQUEST: it writes no preference, so it cannot undo a
   * tap. Absent on a native build older than it, which is why every caller
   * treats a rejection as "this build does not do corrections".
   */
  reapplyRoute(): Promise<AudioRouteStatus>;
  getStatus(): Promise<AudioRouteStatus>;
  /**
   * Fired by the native side whenever the route settles somewhere new — a
   * headset connected or pulled out, an interruption handed the session back,
   * the app returned to the foreground. Emitted AFTER the re-route, so the
   * payload is the EFFECTIVE route rather than the trigger for it.
   *
   * Declared here so `plugin.addListener` is typed; at runtime it is provided
   * by Capacitor's proxy, and an older native build simply never fires it.
   */
  addListener(
    eventName: typeof ROUTE_CHANGE_EVENT,
    listenerFunc: (status: AudioRouteStatus) => void,
  ): Promise<PluginListenerHandle>;
}

/**
 * Queue key for output preference changes.
 *
 * DELIBERATELY NOT 'speakerphone'. Sharing that key would make a preference
 * change wait behind (or be coalesced away by) a session setup/teardown, and
 * those two are not the same intent at all — see setAudioOutput below.
 */
const OUTPUT_QUEUE_KEY = 'audio-output';
/**
 * ITS OWN KEY, and that is the point rather than tidiness.
 *
 * The queue keeps only the NEWEST pending intent per key. A correction sharing
 * the tap's key would therefore supersede a tap that was queued and not yet
 * started — the tap's body would never run, the control would have moved
 * optimistically, and the sound would stay where it was. A correction is not an
 * intent anybody is waiting on; it must not be able to displace one that is.
 */
const REAPPLY_QUEUE_KEY = 'audio-output-reapply';

const NATIVE_PLATFORMS = new Set(['android', 'ios']);
const isNative = NATIVE_PLATFORMS.has(Capacitor.getPlatform());

const plugin = registerPlugin<AudioRoutingPluginInterface>('AudioRouting');

/**
 * Every live subscription's DELIVERY function (the deduped wrapper built in
 * addRouteChangeListener), not the caller's raw listener.
 *
 * WHY A JS-SIDE REGISTRY AT ALL. A route moves for two different reasons and
 * only one of them has a promise waiting on it. The OS moving it — a headset
 * connected, an interruption handing the session back — is answered by the
 * native `routeChange` event. A PARTICIPANT moving it is answered by
 * setAudioOutput's own promise, which reaches the component that tapped and
 * nobody else: any other subscriber (the Speaker toggle on another screen, the
 * dev sound panel), holding no promise, went on showing the previous route
 * until its next poll. (Codex P2 on
 * PR #871, id 3981030948.)
 *
 * THE EMISSION POINT IS HERE, not in the natives. Both were possible; this one
 * was chosen because it gives exactly ONE emitter per kind of transition — the
 * natives emit only for changes the OS initiated, the service emits only for
 * the one it initiated itself — so no transition has two sources that have to
 * be kept from firing twice. It is also the only one of the two whose contract
 * a JS test can actually prove: a litmus over a native `notifyListeners` line
 * can only assert that a mock was called by hand.
 */
const routeChangeDeliveries = new Set<(status: AudioRouteStatus) => void>();

/**
 * A stable string for "this is the same status as last time".
 *
 * SORTED KEYS on purpose. The two producers are a Swift `[String: Any]` and an
 * Android `JSObject`, and neither promises a key ORDER across calls, so a plain
 * JSON.stringify would call two byte-identical payloads different and defeat
 * the dedupe silently. Every field in the payload is a flat scalar, so this is
 * a total comparison rather than a shallow one.
 */
function statusFingerprint(status: AudioRouteStatus): string {
  return JSON.stringify(status, Object.keys(status).sort());
}

/**
 * Hand one status to every live subscription.
 *
 * Iterates a COPY: a listener is allowed to unsubscribe from inside its own
 * callback (a component unmounting on the state change it just received), and
 * mutating the set mid-iteration is undefined behaviour.
 */
function dispatchRouteChange(status: AudioRouteStatus): void {
  for (const deliver of [...routeChangeDeliveries]) deliver(status);
}

/**
 * THE publication point for a route THIS SERVICE moved. Every route-changing
 * call ends here.
 *
 * One function rather than one per caller (class C/D). setAudioOutput published
 * and setSpeakerphoneOn did not, so which subscribers learned about a move
 * depended on which entry point made it — and the entry point nobody wired up
 * was the one that runs at the START of every circle, where the toggle is
 * mounting at the same moment. Anything that can move the route publishes
 * through here; there is no second way to tell a subscriber anything.
 *
 * Takes a status rather than reading one, because the two callers already have
 * different answers to "where does the status come from" and only one of them
 * needs a bridge call — see publishRouteAfterApply below.
 */
function publishRoute(
  status: AudioRouteStatus | null | undefined,
  { armSettleReads = true }: { armSettleReads?: boolean } = {},
): void {
  if (!status) return;
  dispatchRouteChange(status);
  if (armSettleReads) scheduleSettleReads();
}

/**
 * When, after a route announcement, the route is read AGAIN.
 *
 * THE BUG (David, 1:1 call on Android, 2026-10-30): "headphone symbol didn't
 * show. when i clicked on the symbol before it (which looked like a volume
 * button) it changed to a headphone symbol (i was already on headphones the
 * whole time)." The control showed the route from a snapshot, and the route
 * moved onto the headset after it with nothing announcing the move; the tap
 * did not move the audio, it only made `setAudioOutput` read the route again.
 *
 * Every announcement the control receives is a snapshot of a target that is
 * still moving: Android applies a communication-device request on its own
 * schedule (450-490 ms measured on an SM-G981U1, see `applyPreferred`), the
 * mode-owner recompute lands in that window, and below API 31 a Bluetooth SCO
 * link comes up a second or more after `startBluetoothSco()` with no callback
 * at all. The natives now announce every communication-device move they can
 * observe (AudioRoutingPlugin's listeners); this is the half that does not
 * depend on there being such a callback — an Android 7-11 phone, an OEM build
 * that refuses the listener, an installed native build older than that fix.
 *
 * Two reads: one just past the measured framework window, one past a slow
 * Bluetooth link. Each publishes through the same per-subscriber dedupe, so a
 * read that finds nothing new renders nothing. Bounded: a settle read never
 * schedules another, and nothing is read while nobody is subscribed.
 */
export const ROUTE_SETTLE_READ_DELAYS_MS = [600, 2000] as const;

let settleTimers: ReturnType<typeof setTimeout>[] = [];

/**
 * Bumped at the start of every route TAP (setAudioOutput), so the settle reads
 * can tell whether a tap began after they were armed or started reading.
 *
 * WHY A COUNTER AND NOT ONLY THE CANCEL. Cancelling the timers at the tap stops
 * a read that has not fired yet, and nothing else: a read already crossing the
 * bridge when she taps would still announce the pre-tap route, and an OLDER
 * tap answering while a newer one is in flight would re-arm reads under the
 * newer one, undoing its cancel. Both put the route from before her tap back on
 * the control and make its sequence guard drop the tap's answer (#1160).
 */
let routeTapSeq = 0;

/** True when no tap has started since `tap` did. */
function isLatestTap(tap: number): boolean {
  return tap === routeTapSeq;
}

function cancelSettleReads(): void {
  for (const timer of settleTimers) clearTimeout(timer);
  settleTimers = [];
}

/**
 * (Re)arm the settle reads. Re-arming replaces the previous set rather than
 * adding to it, so a burst of announcements — session setup, the device
 * callbacks, the gate-release re-apply — costs two reads after the LAST one.
 */
function scheduleSettleReads(): void {
  cancelSettleReads();
  if (!isNative || routeChangeDeliveries.size === 0) return;
  settleTimers = ROUTE_SETTLE_READ_DELAYS_MS.map((delay) =>
    setTimeout(() => {
      void settleRead();
    }, delay),
  );
}

async function settleRead(): Promise<void> {
  if (routeChangeDeliveries.size === 0) return;
  const readAtTap = routeTapSeq;
  try {
    const status = await plugin.getStatus();
    // A tap started while this read was on the bridge: what it read is the
    // route from before the tap, and the tap's own answer re-arms the reads.
    if (!isLatestTap(readAtTap)) return;
    // dispatch, NOT publishRoute: a settle read must not re-arm itself.
    if (status) dispatchRouteChange(status);
  } catch {
    // The next announcement, foreground or poll will tell; a failed read is
    // not a route.
  }
}

/**
 * Publish the route a user-initiated switch produced.
 *
 * Re-reads getStatus() rather than synthesising a status from setAudioOutput's
 * `{ output, effective }`. CLASS C: subscribers consume the native
 * statusPayload() shape — the dev sound panel refines `headset` into BT vs
 * WIRED from `hasBluetoothA2dp`, and the toggle reads `hasEarpiece` — and a
 * hand-built partial would be a second, thinner shape for the same fact, which
 * is precisely what statusPayload() was factored out to prevent. One extra
 * bridge call, once per tap, buys one shape.
 *
 * `setSpeakerphoneOn` needs no such read: its own native resolve IS that shape.
 *
 * NEVER THROWS and never changes the outcome of the switch that triggered it:
 * a failed status read means subscribers wait for the next poll or event, which
 * is strictly better than turning a switch that WORKED into a reported failure.
 */
async function publishRouteAfterApply(tap: number): Promise<void> {
  // Nobody is listening — skip the bridge call entirely.
  if (routeChangeDeliveries.size === 0) return;
  try {
    // The route this tap produced is published either way; the settle reads
    // are re-armed only if no newer tap has started, because that tap cancelled
    // them at its start and is still waiting on its own answer.
    publishRoute(await plugin.getStatus(), { armSettleReads: isLatestTap(tap) });
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[AudioRouting] post-switch status read failed:', err);
    }
  }
}

/**
 * Hand back the settle reads a refused tap cancelled at its start.
 *
 * A refusal moved nothing, so there is nothing to publish — but the reads an
 * EARLIER announcement armed were still owed: on an Android 7-11 phone (no
 * communication-device listener) they are the only thing that sees a Bluetooth
 * link come up, so a headset still settling at the tap stayed unannounced until
 * the next event or foreground (backlog c-099). Not when a newer tap has
 * started: it cancelled them at its own start and is still in flight.
 */
function rearmSettleReadsAfterRefusal(tap: number): void {
  if (isLatestTap(tap)) scheduleSettleReads();
}

/**
 * Publish the answer to a route call that is NOT a tap — the call-start
 * re-apply (`applyPreferred`) and session setup/teardown (`setSpeakerphoneOn`)
 * — unless a tap started while it was on the bridge.
 *
 * `sentAtTap` is `routeTapSeq` read BEFORE the bridge call. A tap since then
 * means the native side may have served this call first, so its status is the
 * route from before her tap: published, it lands mid-tap (the toggle's sequence
 * guard then drops the tap's answer) and its re-arm undoes the tap's cancel.
 * Dropped, nothing is lost: the tap's own answer publishes a fresh read and
 * re-arms (`applied`), or re-arms (a refusal). The same rule the settle reads
 * follow (backlog c-134).
 */
function publishRouteUnlessTapped(
  status: AudioRouteStatus | null | undefined,
  sentAtTap: number,
): void {
  if (!isLatestTap(sentAtTap)) return;
  publishRoute(status);
}

export const audioRouting = {
  /**
   * SERIALISED. Two of these could previously be in flight at once — React
   * fires an effect's cleanup and setup back to back, so the bridge received
   * `false` then `true` with nothing ordering them, and if the `false` landed
   * last the audio session stayed torn down for the rest of the circle.
   *
   * On Android that teardown drops MODE_IN_COMMUNICATION entirely; on iOS it
   * sets the category to .ambient and deactivates. Neither is a speaker toggle.
   */
  async setSpeakerphoneOn(enabled: boolean): Promise<void> {
    if (!isNative) return;

    return enqueueAudioSessionTask('speakerphone', async () => {
      try {
        // PUBLISHED, both ways round. Setup moves the route to the loudspeaker
        // and teardown hands it back, and neither transition has a native
        // `routeChange` behind it — the natives emit only for moves the OS
        // initiated. This call is where a circle's route is decided, and it is
        // racing the very control that renders it: AudioOutputToggle reads the
        // route once on mount, CircleSession's effect takes the session, and the
        // mount read normally wins. The control was then showing the earpiece
        // position — unlit — over a live loudspeaker, so the first tap, from
        // someone who reached for it precisely because she was hearing her own
        // feedback, asked for the speaker she already had. (Codex P2 on PR
        // #876, id 3983750990.)
        //
        // Costs NOTHING extra at the bridge: the natives already resolve
        // statusPayload(), the same shape getStatus() and the event carry, so
        // there is no second read to make. `undefined` from a native build that
        // predates the payload publishes nothing, which is the honest answer.
        const sentAtTap = routeTapSeq;
        const status = await plugin.setSpeakerphoneOn({ enabled });
        // THE IN-CALL SESSION JUST CAME UP, OR JUST WENT BACK, and this is the
        // only place in the app that knows either. Both surfaces funnel here —
        // the circle from `CircleSession`'s session effect, the 1:1 from
        // `registerLiveCall()` — so this is where `audioOutputBirth` learns
        // whether creating renderer audio output is safe (class E: one copy of
        // "is the mode set", and it is this one).
        //
        // AFTER the await, deliberately: the native call has RETURNED, so
        // `setMode(MODE_IN_COMMUNICATION)` (Android) / the active
        // `AVAudioSession` (iOS) is a fact and not a request. Nothing is noted
        // on the catch path below — a call that threw set no mode, and a
        // deferred opener released against it would be born on the media
        // strategy, which is the defect this whole mechanism exists to stop.
        if (enabled) noteInCallSessionEstablished();
        else noteInCallSessionReleased();
        publishRouteUnlessTapped(status, sentAtTap);
      } catch (err) {
        // Native plugin missing or threw — log in dev, swallow elsewhere.
        // We never want a routing-toggle failure to break a circle session.
        // Nothing is published on this path: a call that threw moved nothing we
        // can describe, and a guess is worse than the next poll.
        if (import.meta.env.DEV) {
          console.warn('[AudioRouting] setSpeakerphoneOn failed:', err);
        }
      }
    });
  },

  /**
   * Move the call's output between the loudspeaker and the earpiece.
   *
   * THIS IS THE SPEAKER TOGGLE. `setSpeakerphoneOn` is not, and never was:
   * passing it `false` calls setMode(MODE_NORMAL) + clearCommunicationDevice()
   * on Android and sets the category to .ambient + deactivates on iOS. That
   * ends the call's audio rather than moving it, so a participant who wanted
   * out of the loudspeaker would get silence.
   *
   * Sticky for the session on the native side: the choice survives until the
   * session tears down, and the headset connect/disconnect handlers re-apply
   * the PREFERENCE rather than snapping back to the loudspeaker.
   *
   * Reports the EFFECTIVE route — which is not always what was asked for,
   * because a connected headset wins — as one of the four outcomes in
   * SetAudioOutputResult. Never throws and never resolves to a bare route: a
   * caller has to be able to tell a refusal from a supersede, because only one
   * of those is worth interrupting the user about.
   */
  async setAudioOutput(output: PreferredAudioOutput): Promise<SetAudioOutputResult> {
    if (!isNative) return { status: 'unsupported' };

    // SUPERSEDED until proven otherwise. The queue resolves a superseded
    // task's promise WITHOUT running its body, so "the body never assigned
    // anything" is precisely the supersede case — read off the queue's real
    // behaviour rather than inferred from a second flag that could drift out
    // of step with it.
    let outcome: SetAudioOutputResult = { status: 'superseded' };

    // A participant is moving the route NOW. A settle read armed by an earlier
    // announcement could land between her tap and its answer and put the
    // pre-tap route back on the control, so the reads stop here and EVERY
    // answer below hands them back: `applied` through its publish, `failed` and
    // `unsupported` by re-arming directly. Not cancelled only on `applied`
    // instead: the read this exists to stop lands BEFORE the answer, when
    // nobody knows yet which answer it will be.
    const tap = ++routeTapSeq;
    cancelSettleReads();

    // Its own queue key. A preference change and a session setup/teardown are
    // different intents; coalescing them would let a join's setSpeakerphoneOn
    // swallow a user's tap, or make the tap wait on it for no safety gain.
    await enqueueAudioSessionTask(OUTPUT_QUEUE_KEY, async () => {
      try {
        const result = await plugin.setAudioOutput({ output });
        if (result?.outcome === 'unsupported') {
          // The device has no such built-in output — no earpiece on this
          // tablet, no receiver on this iPad. The native side answered the
          // capability question instead of substituting the loudspeaker and
          // calling it a success, which is what both platforms used to do.
          // Nothing moved, so there is nothing to publish below.
          outcome = { status: 'unsupported' };
          rearmSettleReadsAfterRefusal(tap);
          return;
        }
        // `effective` missing means an older native build that predates the
        // field; the request still applied, so report what was asked for.
        outcome = { status: 'applied', effective: result?.effective ?? output };
      } catch (err) {
        // A REFUSAL, and distinct from the supersede default above. Never
        // rethrown: a routing-toggle failure must not break a circle — but it
        // must not be invisible either, which is what the old `null` made it.
        outcome = { status: 'failed' };
        if (import.meta.env.DEV) {
          console.warn('[AudioRouting] setAudioOutput failed:', err);
        }
        rearmSettleReadsAfterRefusal(tap);
        return;
      }
      // TELL EVERYONE ELSE. The caller has this outcome in its own promise;
      // the other subscribers — the dev sound panel an on-device tester
      // reads to decide whether the toggle worked, above all — have no
      // promise and were left on the previous route until their next poll.
      //
      // INSIDE the queued task, so a second tap cannot start while this
      // publish is still reading the route: the status a subscriber receives
      // is the one this switch produced, not one already replaced. Only on
      // `applied`: a refusal moved nothing, so there is nothing to report.
      await publishRouteAfterApply(tap);
    });

    return outcome;
  },

  /**
   * SAY THE ROUTE AGAIN — put the output back on whatever was last asked for,
   * without asking the participant anything.
   *
   * WHY A CALL THAT CHANGES NOTHING EXISTS. Setting up a call is two steps that
   * the Android framework does not perform together:
   * `setMode(MODE_IN_COMMUNICATION)` makes us the mode owner, and
   * `setCommunicationDevice(...)` says where the call should come out. Between
   * them the framework recomputes the route for its new mode owner, and at that
   * instant our device request has not been recorded yet — so it routes with
   * `preferredCommunicationDevice: null`, and the default for a phone call with
   * no stated preference is the EARPIECE.
   *
   * MEASURED, on a real SM-G981U1 (Android 13), 2026-09-15, cold start:
   *
   *   14:17:58.507  setCommunicationDevice deviceType = 2      (ours, speaker)
   *   14:17:58.986  HAL output devices -> 0x1 EARPIECE         (the recompute)
   *   14:17:59.436  HAL output devices -> 0x2 SPEAKER          (ours, finally)
   *
   * A 450 ms window in which the call is on the earpiece with the control
   * saying "Speaker on" — because `getCommunicationDevice()` reports what was
   * REQUESTED and is perfectly honest about it.
   *
   * THE CIRCLE NEVER NOTICED, and that is the whole difference between the two
   * surfaces. Its first sound is seconds behind this window — a websocket, an
   * attach, a join, an offer, then somebody speaks — so the window has closed
   * long before there is anything to hear. The 1:1's first sound lands INSIDE
   * it: the partner's stream is released the moment the microphone is up, which
   * is the same moment. Whatever output the platform has open then is what the
   * call plays out of, and it stays there until something forces a re-route —
   * which is what two taps of the Speaker control were doing.
   *
   * NO FALLBACK when the native build cannot say what was preferred. The
   * obvious one — re-apply the EFFECTIVE route — is exactly wrong here: the
   * effective route during that window is the earpiece, and re-applying it
   * would cement the bug rather than fix it. An older build simply does not get
   * this correction.
   *
   * Goes through `setAudioOutput`, so it takes the same queue, the same native
   * `applyRoute`, the same headset rule (a headset wins; the native side owns
   * that and there is no second copy of it here) and the same publication.
   */
  async applyPreferred(): Promise<SetAudioOutputResult> {
    if (!isNative) return { status: 'unsupported' };

    let outcome: SetAudioOutputResult = { status: 'superseded' };
    await enqueueAudioSessionTask(REAPPLY_QUEUE_KEY, async () => {
      try {
        const sentAtTap = routeTapSeq;
        const status = await plugin.reapplyRoute();
        outcome = { status: 'applied', effective: status?.output ?? 'speaker' };
        publishRouteUnlessTapped(status, sentAtTap);
      } catch (err) {
        // A native build older than `reapplyRoute`, or a platform that refused.
        // Neither is worth telling anybody about: the correction is hygiene, and
        // the call is audible either way.
        outcome = { status: 'unsupported' };
        if (import.meta.env.DEV) {
          console.warn('[AudioRouting] reapplyRoute failed:', err);
        }
      }
    });
    return outcome;
  },

  async setKeepScreenOn(enabled: boolean): Promise<void> {
    if (!isNative) return;
    try {
      await plugin.setKeepScreenOn({ enabled });
    } catch (err) {
      if (import.meta.env.DEV) {
        console.warn('[AudioRouting] setKeepScreenOn failed:', err);
      }
    }
  },

  /**
   * Keep the live call's audio running while the app is backgrounded or the
   * screen is LOCKED (beta #8). On Android this starts a foreground microphone
   * service (a persistent "call in progress" notification) so the OS doesn't
   * suspend the process and kill WebRTC. On iOS the active playAndRecord
   * session plus the `audio` UIBackgroundMode is what keeps an active call
   * alive across lock, so the native side activates the session.
   *
   * NOT A NO-OP ON iOS, whatever this comment said before. The iOS plugin also
   * re-applies the route, deliberately: in the meditation phase this is the
   * ONLY call that runs — setSpeakerphoneOn does not fire until the speaking
   * phase mounts the Janus room — so without it a connected headset is ignored
   * and meditation plays out the loudspeaker (beta #1). Because it moves the
   * route, it now emits `routeChange` like every other unasked-for move, and
   * the toggle updates itself.
   * Always pair with stopBackgroundAudio() when the call ends.
   */
  async startBackgroundAudio(title = 'Empathy call', text = 'Call in progress'): Promise<void> {
    if (!isNative) return;

    // QUEUED, under its own key. This pair had the exact fire-and-forget shape
    // the queue was built to remove, and was the only session call still
    // missing it — so a stop/start issued back to back could land in either
    // order and leave the foreground service stopped during a live call, taking
    // the Android communication mode and audio focus with it. The queue's own
    // test suite already had a 'background' key case; it was simply never
    // wired. (Codex P1 on PR #800.)
    //
    // A separate key from 'speakerphone': the two are independent, and making
    // one wait on the other would add latency for no safety.
    await enqueueAudioSessionTask('background', async () => {
      try {
        await plugin.startBackgroundAudio({ title, text });
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[AudioRouting] startBackgroundAudio failed:', err);
        }
      }
    });
  },

  async stopBackgroundAudio(): Promise<void> {
    if (!isNative) return;

    await enqueueAudioSessionTask('background', async () => {
      try {
        await plugin.stopBackgroundAudio();
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[AudioRouting] stopBackgroundAudio failed:', err);
        }
      }
    });
  },

  // Return type derived from the plugin interface rather than restated. The
  // hand-written copy had already drifted: hasAudioFocus/lastFocusChange were
  // added to the plugin and omitted here, so the fields the ducking diagnostic
  // depends on were typed out of existence for every caller. They survived at
  // runtime only by structural-typing accident.
  async getStatus(): Promise<Awaited<ReturnType<AudioRoutingPluginInterface['getStatus']>> | null> {
    if (!isNative) return null;
    try {
      return await plugin.getStatus();
    } catch {
      return null;
    }
  },

  /**
   * Follow the route while it moves, instead of asking and hoping.
   *
   * THE BUG. AudioOutputToggle learned the route exactly twice: on mount, and
   * on `visibilitychange`. A headset plugged in or pulled out DURING a circle
   * moves the effective route with the app in the foreground the whole time —
   * so neither of those fires, and the control keeps rendering the previous
   * world. A live speaker/earpiece button while audio is already on the
   * headset, or a read-only headset badge minutes after the headset came out.
   * The person finds out by tapping a control that cannot do what it offers.
   * (Codex P2 on PR #871.)
   *
   * The native side now emits `routeChange` AFTER its re-route settles, with
   * the same payload `getStatus()` answers — so a subscriber and a poller
   * cannot disagree about the device.
   *
   * TWO SOURCES, ONE DOOR. The native event covers every change the OS
   * initiated; this service publishes the one a PARTICIPANT initiated, after
   * setAudioOutput has settled, because that transition has no native callback
   * behind it and its promise reaches only the component that tapped. Both
   * arrive through the same deduped delivery below, so a subscriber cannot tell
   * — or need to tell — which of the two moved the route.
   *
   * NEVER THROWS, on any platform. Off native there is no plugin to subscribe
   * to and this is a no-op; a native build too old to know the event simply
   * never fires it; and if the bridge refuses the subscription outright, a
   * caller keeping its `visibilitychange` fallback is strictly better off than
   * one taking an exception. Callers get a remove handle either way, so the
   * unsubscribe path never has to branch on any of that.
   *
   * @returns a function that removes the listener. Idempotent, and safe to
   *          call before the (asynchronous) subscription has even resolved —
   *          which is the unmount-immediately case, and the one that leaks.
   */
  addRouteChangeListener(listener: (status: AudioRouteStatus) => void): () => void {
    if (!isNative) return () => {};

    let handle: PluginListenerHandle | undefined;
    let removed = false;
    /** Fingerprint of the last status this subscription actually delivered. */
    let lastDelivered: string | null = null;

    /**
     * The one door into this subscription, for BOTH sources — the native event
     * and the service's own post-switch publish.
     *
     * DEDUPES on the payload. The two sources describe disjoint transitions by
     * design, but iOS has one narrow path where they can overlap: if the WebRTC
     * stack has moved the category, a user-initiated applyRoute() re-asserts
     * .playAndRecord, and that posts a .categoryChange the route-change handler
     * answers with an emit of its own — the same transition, twice. Suppressing
     * a status identical to the one just delivered costs a duplicate beacon row
     * in the worst case and removes the double render.
     *
     * ALSO the isolation boundary: one subscriber throwing must not stop the
     * others being told, and must not turn a successful switch into a failure.
     */
    const deliver = (status: AudioRouteStatus) => {
      if (!status) return;
      const print = statusFingerprint(status);
      if (print === lastDelivered) return;
      lastDelivered = print;
      try {
        listener(status);
      } catch (err) {
        if (import.meta.env.DEV) {
          console.warn('[AudioRouting] routeChange listener threw:', err);
        }
      }
    };
    routeChangeDeliveries.add(deliver);

    try {
      void plugin
        // A native announcement is a snapshot too (see
        // ROUTE_SETTLE_READ_DELAYS_MS), so it arms the settle reads as well.
        .addListener(ROUTE_CHANGE_EVENT, (status: AudioRouteStatus) => {
          deliver(status);
          scheduleSettleReads();
        })
        .then((h) => {
          // Unmounted while the subscription was in flight. Removing the
          // handle we were just given is the only thing that can unregister
          // it — the caller's remove ran before there was anything to remove.
          if (removed) {
            void h.remove();
            return;
          }
          handle = h;
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.warn('[AudioRouting] routeChange subscribe failed:', err);
          }
        });
    } catch (err) {
      // Synchronous throw: a native build (or a test double) with no
      // addListener at all. The caller still gets a working remove handle.
      if (import.meta.env.DEV) {
        console.warn('[AudioRouting] routeChange subscribe unavailable:', err);
      }
    }

    return () => {
      removed = true;
      // Off the JS registry FIRST and synchronously: the native handle's
      // removal is asynchronous and best-effort, and the service's own publish
      // must stop reaching an unmounted component the instant it says stop.
      routeChangeDeliveries.delete(deliver);
      if (routeChangeDeliveries.size === 0) cancelSettleReads();
      try {
        void handle?.remove();
      } catch {
        /* removing a listener must not be able to break a teardown */
      }
    };
  },

  isAvailable(): boolean {
    return isNative;
  },
};
